import { useMemo, useState } from 'react';
import { Tools } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { MessageGeneration } from '../types/chat';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { CHAT_MESSAGE_ACTION_ICON_PROPS } from 'constant/chat-layout';
import { groupMessageToolCallsByToolId } from '../utils/parse-message-tool-calls';

interface MessageToolCallsProps {
  message: MessageGeneration;
}

const formatToolCallDuration = (durationMs?: number): string => {
  if (durationMs == null) {
    return '—';
  }
  if (durationMs >= 1000) {
    return `${(durationMs / 1000).toFixed(2)}s`;
  }
  return `${durationMs}ms`;
};

const formatGroupedToolLabel = (toolName: string, count: number): string =>
  count > 1 ? `${toolName}(${count}x)` : toolName;

export function MessageToolCalls({ message }: MessageToolCallsProps) {
  const { t } = useTranslation();
  const toolCalls = message.toolCalls;
  const [isOpen, setIsOpen] = useState(false);

  const groupedToolCalls = useMemo(
    () => (toolCalls ? groupMessageToolCallsByToolId(toolCalls) : []),
    [toolCalls]
  );

  const totalDurationMs = useMemo(
    () => groupedToolCalls.reduce((sum, call) => sum + (call.totalDurationMs ?? 0), 0),
    [groupedToolCalls]
  );

  const hasAnyDuration = groupedToolCalls.some((call) => call.totalDurationMs != null);
  const showTotalRow = groupedToolCalls.length > 1 && hasAnyDuration;
  const invocationCount = toolCalls?.length ?? 0;

  if (!toolCalls?.length) {
    return null;
  }

  const callCountLabel =
    invocationCount === 1
      ? t('TOOL_CALL_COUNT_ONE') || '1 call'
      : t('TOOL_CALL_COUNT_MANY', { count: invocationCount }) || `${invocationCount} calls`;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          <div className="relative inline-block">
            <button
              type="button"
              className="relative inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted"
              onClick={(e) => e.stopPropagation()}
              title={t('TOOLS') || 'Tools'}
              aria-label={t('TOOLS') || 'Tools'}
            >
              <Tools {...CHAT_MESSAGE_ACTION_ICON_PROPS} />

            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="w-[min(100vw-2rem,288px)] p-0">
          <div className="flex flex-col overflow-hidden rounded-md">
            <div className="flex items-center justify-between gap-3 border-b border-primary-foreground/20 px-3 py-2">
              <span className="text-xs font-semibold">
                {t('MESSAGE_TOOL_CALLS') || 'Tool calls'}
              </span>

            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-b border-primary-foreground/10 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide opacity-60">
              <span>{t('TOOL_NAME') || 'Tool'}</span>
              <span className="text-right">{t('TOOL_CALL_DURATION') || 'Duration'}</span>
            </div>

            <div className="max-h-[min(50vh,220px)] overflow-y-auto overscroll-contain">
              {groupedToolCalls.map((call, index) => (
                <div
                  key={call.toolId}
                  className={cn(
                    'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 px-3 py-2 text-xs',
                    index % 2 === 1 && 'bg-primary-foreground/5',
                    index < groupedToolCalls.length - 1 && 'border-b border-primary-foreground/10'
                  )}
                >
                  <span className="truncate font-medium" title={formatGroupedToolLabel(call.toolName, call.count)}>
                    {call.toolName}
                    {call.count > 1 && (
                      <span className="font-normal opacity-80">({call.count}x)</span>
                    )}
                  </span>
                  <span className="shrink-0 text-right font-medium tabular-nums">
                    {formatToolCallDuration(call.totalDurationMs)}
                  </span>
                </div>
              ))}
            </div>

            {showTotalRow && (
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 border-t border-primary-foreground/20 bg-primary-foreground/10 px-3 py-2 text-xs">
                <span className="font-medium opacity-90">{t('TOTAL') || 'Total'}</span>
                <span className="shrink-0 text-right font-semibold tabular-nums">
                  {formatToolCallDuration(totalDurationMs)}
                </span>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
